import { memo } from 'react';
import styled from 'styled-components';
import { Skeleton, theme } from '@frameio/vapor';
import { ImageWrapper } from '../../../../shared/components';
import type { ListMode } from '../../../hooks';
import { QuickSearchListDivider } from '../../shared';

type ListSkeletonProps = {
  mode?: ListMode;
};

/**
 * Renders a skeleton loading state for the Quick Search list.
 *
 * It displays a fixed number of `ListItemSkeleton` placeholders to visually
 * represent the list items that are being loaded in the background. This component
 * is memoized to prevent unnecessary re-renders.
 *
 * In `MULTI` mode, it renders two sections with a divider between them to match
 * the actual multi-section layout.
 */
export const ListSkeleton = memo(({ mode = 'SINGLE' }: ListSkeletonProps) => {
  if (mode === 'MULTI') {
    return (
      <ListSkeletonContainer>
        {/* First section (e.g., Quick Results) */}
        <SkeletonSection>
          <SkeletonSectionHeader>
            <Skeleton height="8px" width="15%" />
          </SkeletonSectionHeader>
          <SkeletonItemsWrapper>
            <ListItemSkeleton />
            <ListItemSkeleton />
            <ListItemSkeleton />
          </SkeletonItemsWrapper>
        </SkeletonSection>

        {/* Divider - with 16px padding equivalent to dividerConfig */}
        <QuickSearchListDivider paddingX={16} style={{ paddingBlock: 7 }} />

        {/* Second section (e.g., AI Results) */}
        <SkeletonSection>
          <SkeletonSectionHeader>
            <Skeleton height="8px" width="20%" />
          </SkeletonSectionHeader>
          <SkeletonItemsWrapper>
            <ListItemSkeleton />
            <ListItemSkeleton />
            <ListItemSkeleton />
          </SkeletonItemsWrapper>
        </SkeletonSection>
      </ListSkeletonContainer>
    );
  }

  return (
    <ListSkeletonContainer>
      <SkeletonItemsWrapper>
        <ListItemSkeleton />
        <ListItemSkeleton />
        <ListItemSkeleton />
        <ListItemSkeleton />
        <ListItemSkeleton />
        <ListItemSkeleton />
      </SkeletonItemsWrapper>
    </ListSkeletonContainer>
  );
});

ListSkeleton.displayName = 'ListSkeleton';

/**
 * Renders the skeleton placeholder for a single item in the Quick Search list.
 *
 * This component mimics the layout of a real list item, with placeholders for a
 * thumbnail and textual details (like title and subtitle). It is memoized for
 * performance.
 */
const ListItemSkeleton = memo(() => (
  <ListItemWrapper>
    <ImageWrapper borderRadius={4} hasBorder={false}>
      <ListItemImageSkeleton>
        <Skeleton height="100%" style={{ lineHeight: 'normal' }} width="100%" />
      </ListItemImageSkeleton>
    </ImageWrapper>
    <ListItemDetailsSkeleton>
      <Skeleton height="12px" width="100%" />
      <Skeleton height="8px" width="50%" />
    </ListItemDetailsSkeleton>
  </ListItemWrapper>
));

ListItemSkeleton.displayName = 'ListItemSkeleton';

const ListItemDetailsSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${theme.spaces[12]};
  padding-bottom: ${theme.spaces[8]};
  padding-left: ${theme.spaces[8]};
  padding-top: ${theme.spaces[8]};
`;

const ListSkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const SkeletonItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-inline: ${theme.spaces[12]};
  width: 100%;

  @media (min-width: ${theme.breakpoints.tablet}) {
    padding-inline: ${theme.spaces[16]};
  }
`;

const ListItemWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: ${theme.spaces[12]};
  height: 77px;
  padding-block: ${theme.spaces[8]};

  @media (min-width: ${theme.breakpoints.tablet}) {
    align-items: flex-start;
    height: auto;
  }
`;

const ListItemImageSkeleton = styled.div`
  height: 60px;
  width: 90px;

  @media (min-width: ${theme.breakpoints.tablet}) {
    height: 90px;
    width: 160px;
  }
`;

const SkeletonSectionHeader = styled.div`
  align-items: center;
  display: flex;
  min-height: 36px;
  padding-bottom: ${theme.spaces[8]};
  padding-left: ${theme.spaces[16]};
  padding-right: ${theme.spaces[8]};
  padding-top: ${theme.spaces[8]};
`;

const SkeletonSection = styled.div`
  display: flex;
  flex-direction: column;
`;
